import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { colors } from "src/styles";

/* ============================================================
 * Types
 * ============================================================ */

export type TimeLogSeverity = "high" | "medium" | "clean";
export type TimeLogStatus = "pending" | "approved" | "noshow";
export type PayrollPeriodStatus = "Open" | "Submitted";

export interface Employee {
  id: string;
  name: string;
  role: string;
  type: "hourly" | "fixed";
  hourlyRate?: number;
  vacationRate?: number;
}

export interface Shift {
  id: string;
  employeeId: string;
  clientName: string;
  plannedStart: string;
  plannedEnd: string;
  plannedHours: number;
}

export interface TimeLog {
  id: string;
  employeeId: string;
  shiftId?: string;
  clientName: string;
  date: string;
  actualStart?: string;
  actualEnd?: string;
  actualHours: number;
  plannedHours: number;
  severity: TimeLogSeverity;
  status: TimeLogStatus;
  approvedHours?: number;
  note?: string | null;
}

export interface TimeOffEntry {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  type: string;
  status: "pending" | "approved" | "rejected";
}

export interface PayrollPeriod {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: PayrollPeriodStatus;
  submittedAt?: string;
  submittedBy?: string;
  totals?: {
    byClient: Record<string, { hours: number; cost: number }>;
    byEmployee: Record<string, { hours: number; cost: number }>;
  };
}

/* ============================================================
 * Helpers
 * ============================================================ */

function hourlyRateOf(e: Employee): number {
  return e.hourlyRate ?? 0;
}

function fmtHours(h: number): string {
  return `${h.toFixed(1)}h`;
}

function fmtMoneyUSD(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function fmtRangeCompact(start: string, end: string): string {
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    const suffix = h >= 12 ? "pm" : "am";
    const hour = h % 12 || 12;
    return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, "0")}${suffix}`;
  };
  return `${fmt(start)}–${fmt(end)}`;
}

function logCost(l: TimeLog, e: Employee): number {
  const rate = hourlyRateOf(e);
  if (l.status === "approved") return (l.approvedHours ?? 0) * rate;
  if (l.status === "noshow") return 0;
  return l.actualHours * rate;
}

function minutesBetween(a: string, b: string): number {
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  let diff = bh * 60 + bm - (ah * 60 + am);
  if (diff < -12 * 60) diff += 24 * 60;
  if (diff > 12 * 60) diff -= 24 * 60;
  return diff;
}

function fmtDelta(min: number | null): string {
  if (min == null) return "—";
  if (Math.abs(min) < 1) return "on time";
  return `${min > 0 ? "+" : ""}${min}m`;
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function shortPeriodLabel(p: PayrollPeriod): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const start = fmt(p.periodStart);
  const endParts = p.periodEnd.split("-").map(Number);
  const endDay = endParts[2];
  const startMonth = p.periodStart.slice(0, 7);
  const endMonth = p.periodEnd.slice(0, 7);
  return startMonth === endMonth ? `${start} – ${endDay}` : `${start} – ${fmt(p.periodEnd)}`;
}

// function fmtPeriodRange(p: PayrollPeriod): string {
//   return `${shortDate(p.periodStart)} – ${shortDate(p.periodEnd)}`;
// }

const SEVERITY_META: Record<TimeLogSeverity, { label: string; subtitle: string }> = {
  high: { label: "High", subtitle: "Manual review required" },
  medium: { label: "Medium", subtitle: "Batch review" },
  clean: { label: "Clean", subtitle: "Bulk approve eligible" },
};

/* ============================================================
 * Severity badge colours
 * ============================================================ */
const severityColor: Record<TimeLogSeverity, string> = {
  high: colors.red.default,
  medium: colors.yellow.default,
  clean: colors.green.default,
};

/* ============================================================
 * Props
 * ============================================================ */

interface PayrollApprovalProps {
  periods?: PayrollPeriod[];
  timeLogs?: TimeLog[];
  employees?: Employee[];
  shifts?: Shift[];
  timeOff?: TimeOffEntry[];
  onApproveLog?: (logId: string, hours: number) => void;
  onApproveMany?: (logIds: string[]) => void;
  onApproveManyAtPlanned?: (logIds: string[]) => void;
  onMarkNoShow?: (logId: string) => void;
  onResetLog?: (logId: string) => void;
  onSubmitPeriod?: (periodId: string) => void;
  onSetNote?: (logId: string, note: string | null) => void;
}

/* ============================================================
 * Main Page Component
 * ============================================================ */

// Mock data for demo/development
const MOCK_EMPLOYEES: Employee[] = [
  { id: "e1", name: "Sarah Johnson", role: "Site Manager", type: "hourly", hourlyRate: 28 },
  { id: "e2", name: "Marcus Lee", role: "Supervisor", type: "hourly", hourlyRate: 24 },
  { id: "e3", name: "Emily Chen", role: "Technician", type: "hourly", hourlyRate: 20 },
  { id: "e4", name: "David Martinez", role: "Lead", type: "hourly", hourlyRate: 26 },
];

const MOCK_SHIFTS: Shift[] = [
  { id: "s1", employeeId: "e1", clientName: "Acme Corp", plannedStart: "08:00", plannedEnd: "17:00", plannedHours: 8 },
  { id: "s2", employeeId: "e2", clientName: "Acme Corp", plannedStart: "08:00", plannedEnd: "17:00", plannedHours: 8 },
  { id: "s3", employeeId: "e3", clientName: "TechVentures", plannedStart: "09:00", plannedEnd: "18:00", plannedHours: 8 },
  { id: "s4", employeeId: "e4", clientName: "Global Inc", plannedStart: "07:00", plannedEnd: "15:00", plannedHours: 8 },
  { id: "s5", employeeId: "e1", clientName: "Acme Corp", plannedStart: "08:00", plannedEnd: "17:00", plannedHours: 8 },
];

const MOCK_TIME_LOGS: TimeLog[] = [
  // High severity (late arrival, early departure, no-show)
  {
    id: "t1",
    employeeId: "e1",
    shiftId: "s1",
    clientName: "Acme Corp",
    date: "2026-06-23",
    actualStart: "08:45",
    actualEnd: "17:00",
    actualHours: 8.25,
    plannedHours: 8,
    severity: "high",
    status: "pending",
  },
  {
    id: "t2",
    employeeId: "e2",
    shiftId: "s2",
    clientName: "Acme Corp",
    date: "2026-06-23",
    actualStart: "08:00",
    actualEnd: "16:30",
    actualHours: 8.5,
    plannedHours: 8,
    severity: "high",
    status: "pending",
  },
  {
    id: "t3",
    employeeId: "e3",
    shiftId: "s3",
    clientName: "TechVentures",
    date: "2026-06-23",
    actualHours: 0,
    plannedHours: 8,
    severity: "high",
    status: "pending",
  },
  // Medium severity
  {
    id: "t4",
    employeeId: "e4",
    shiftId: "s4",
    clientName: "Global Inc",
    date: "2026-06-23",
    actualStart: "07:05",
    actualEnd: "15:00",
    actualHours: 8,
    plannedHours: 8,
    severity: "medium",
    status: "pending",
  },
  {
    id: "t5",
    employeeId: "e1",
    shiftId: "s5",
    clientName: "Acme Corp",
    date: "2026-06-24",
    actualStart: "07:55",
    actualEnd: "17:05",
    actualHours: 9.15,
    plannedHours: 8,
    severity: "medium",
    status: "pending",
  },
  // Clean (approved and no-show examples)
  {
    id: "t6",
    employeeId: "e2",
    clientName: "Acme Corp",
    date: "2026-06-24",
    actualStart: "08:00",
    actualEnd: "17:00",
    actualHours: 8,
    plannedHours: 8,
    severity: "clean",
    status: "approved",
    approvedHours: 8,
  },
  {
    id: "t7",
    employeeId: "e3",
    clientName: "TechVentures",
    date: "2026-06-24",
    actualHours: 0,
    plannedHours: 8,
    severity: "clean",
    status: "noshow",
  },
  {
    id: "t8",
    employeeId: "e4",
    shiftId: "s4",
    clientName: "Global Inc",
    date: "2026-06-24",
    actualStart: "07:00",
    actualEnd: "15:00",
    actualHours: 8,
    plannedHours: 8,
    severity: "clean",
    status: "approved",
    approvedHours: 8,
  },
  // More clean entries
  {
    id: "t9",
    employeeId: "e1",
    shiftId: "s1",
    clientName: "Acme Corp",
    date: "2026-06-25",
    actualStart: "08:00",
    actualEnd: "17:00",
    actualHours: 8,
    plannedHours: 8,
    severity: "clean",
    status: "approved",
    approvedHours: 8,
  },
  {
    id: "t10",
    employeeId: "e2",
    shiftId: "s2",
    clientName: "Acme Corp",
    date: "2026-06-25",
    actualStart: "08:00",
    actualEnd: "17:00",
    actualHours: 8,
    plannedHours: 8,
    severity: "clean",
    status: "approved",
    approvedHours: 8,
  },
];

const MOCK_TIME_OFF: TimeOffEntry[] = [
  {
    id: "to1",
    employeeId: "e1",
    startDate: "2026-06-26",
    endDate: "2026-06-26",
    hoursPerDay: 8,
    type: "paid",
    status: "approved",
  },
];

const MOCK_PERIODS: PayrollPeriod[] = [
  {
    id: "p1",
    periodStart: "2026-06-16",
    periodEnd: "2026-06-30",
    status: "Open",
  },
  {
    id: "p2",
    periodStart: "2026-06-02",
    periodEnd: "2026-06-15",
    status: "Submitted",
    submittedAt: "2026-06-16T14:30:00Z",
    submittedBy: "John Admin",
    totals: {
      byClient: {
        "Acme Corp": { hours: 160, cost: 4480 },
        "TechVentures": { hours: 80, cost: 1600 },
        "Global Inc": { hours: 120, cost: 3120 },
      },
      byEmployee: {
        "Sarah Johnson": { hours: 80, cost: 2240 },
        "Marcus Lee": { hours: 80, cost: 1920 },
        "Emily Chen": { hours: 80, cost: 1600 },
        "David Martinez": { hours: 120, cost: 3120 },
      },
    },
  },
];

export const PayrollApproval: React.FC<PayrollApprovalProps> = ({
  periods = MOCK_PERIODS,
  timeLogs = MOCK_TIME_LOGS,
  employees = MOCK_EMPLOYEES,
  shifts = MOCK_SHIFTS,
  timeOff = MOCK_TIME_OFF,
  onApproveLog,
  onApproveMany,
  onApproveManyAtPlanned,
  onMarkNoShow,
  onResetLog,
  onSubmitPeriod,
  onSetNote,
}) => {
  const openPeriod = useMemo(() => periods.find((p) => p.status === "Open") ?? periods[0], [periods]);

  const [periodId, setPeriodId] = useState<string>(openPeriod?.id ?? "");
  const period = periods.find((p) => p.id === periodId) ?? openPeriod;

  const [search, setSearch] = useState("");
  const [investigateEmpId, setInvestigateEmpId] = useState<string | null>(null);
  const [submitArmed, setSubmitArmed] = useState(false);
  const [localLogs, setLocalLogs] = useState<TimeLog[]>(timeLogs);

  useEffect(() => {
    setLocalLogs(timeLogs);
  }, [timeLogs]);

  const empById = useMemo(() => {
    const m: Record<string, Employee> = {};
    for (const e of employees) m[e.id] = e;
    return m;
  }, [employees]);

  const shiftById = useMemo(() => {
    const m: Record<string, Shift> = {};
    for (const s of shifts) m[s.id] = s;
    return m;
  }, [shifts]);

  const periodLogs = useMemo(() => {
    if (!period) return [];
    return localLogs.filter((l) => l.date >= period.periodStart && l.date <= period.periodEnd);
  }, [localLogs, period]);

  const periodTimeOff = useMemo(() => {
    if (!period) return [] as TimeOffEntry[];
    return timeOff.filter(
      (t) => t.status === "approved" && t.endDate >= period.periodStart && t.startDate <= period.periodEnd,
    );
  }, [timeOff, period]);

  const timeOffRollup = useMemo(() => {
    if (!period || periodTimeOff.length === 0) return { hours: 0, cost: 0, paidEntries: 0, unpaidEntries: 0 };
    let hours = 0;
    let cost = 0;
    let paidEntries = 0;
    let unpaidEntries = 0;
    for (const t of periodTimeOff) {
      let days = 0;
      const start = t.startDate < period.periodStart ? period.periodStart : t.startDate;
      const end = t.endDate > period.periodEnd ? period.periodEnd : t.endDate;
      for (let d = start; d <= end; ) {
        const [y, m, dd] = d.split("-").map(Number);
        const dt = new Date(y, m - 1, dd);
        const wd = dt.getDay();
        if (wd !== 0 && wd !== 6) days++;
        dt.setDate(dt.getDate() + 1);
        d = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      }
      const entryHours = days * t.hoursPerDay;
      hours += entryHours;
      const emp = empById[t.employeeId];
      const isPaid = t.type !== "unpaid";
      if (isPaid) {
        paidEntries++;
        const rate = emp ? (emp.vacationRate ?? hourlyRateOf(emp)) : 0;
        cost += entryHours * rate;
      } else {
        unpaidEntries++;
      }
    }
    return { hours, cost, paidEntries, unpaidEntries };
  }, [period, periodTimeOff, empById]);

  const buckets = useMemo(() => {
    const high: TimeLog[] = [];
    const medium: TimeLog[] = [];
    const clean: TimeLog[] = [];
    for (const l of periodLogs) {
      if (l.severity === "high") high.push(l);
      else if (l.severity === "medium") medium.push(l);
      else clean.push(l);
    }
    const sortFn = (a: TimeLog, b: TimeLog) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.employeeId.localeCompare(b.employeeId);
    };
    return {
      high: high.sort(sortFn),
      medium: medium.sort(sortFn),
      clean: clean.sort(sortFn),
    };
  }, [periodLogs]);

  const rollups = useMemo(() => {
    let totalHours = 0;
    let totalCost = 0;
    let pending = 0;
    let approved = 0;
    let highPending = 0;
    const empSet = new Set<string>();
    for (const l of periodLogs) {
      const e = empById[l.employeeId];
      if (!e) continue;
      empSet.add(l.employeeId);
      const hours = l.status === "approved" ? (l.approvedHours ?? 0) : l.status === "noshow" ? 0 : l.actualHours;
      totalHours += hours;
      totalCost += logCost(l, e);
      if (l.status === "pending") pending++;
      if (l.status === "approved" || l.status === "noshow") approved++;
      if (l.status === "pending" && l.severity === "high") highPending++;
    }
    return {
      totalHours,
      totalCost,
      pending,
      approved,
      highPending,
      totalEntries: periodLogs.length,
      employeeCount: empSet.size,
    };
  }, [periodLogs, empById]);

  const searchMatches = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    const counts: Record<string, { entries: number; approved: number }> = {};
    for (const l of periodLogs) {
      counts[l.employeeId] = counts[l.employeeId] ?? { entries: 0, approved: 0 };
      counts[l.employeeId].entries++;
      if (l.status === "approved" || l.status === "noshow") counts[l.employeeId].approved++;
    }
    return employees
      .filter((e) => e.name.toLowerCase().includes(q) && counts[e.id])
      .slice(0, 8)
      .map((e) => ({ employee: e, ...counts[e.id] }));
  }, [search, employees, periodLogs]);

  // Local action handlers (update local state; call parent callbacks too)
  const handleApproveLog = (logId: string, hours: number) => {
    setLocalLogs((prev) =>
      prev.map((l) => (l.id === logId ? { ...l, status: "approved" as TimeLogStatus, approvedHours: hours } : l)),
    );
    onApproveLog?.(logId, hours);
  };

  const handleSetNote = (logId: string, note: string | null) => {
    setLocalLogs((prev) => prev.map((l) => (l.id === logId ? { ...l, note } : l)));
    onSetNote?.(logId, note);
  };

  const handleMarkNoShow = (logId: string) => {
    setLocalLogs((prev) => prev.map((l) => (l.id === logId ? { ...l, status: "noshow" as TimeLogStatus } : l)));
    onMarkNoShow?.(logId);
  };

  const handleResetLog = (logId: string) => {
    setLocalLogs((prev) =>
      prev.map((l) => (l.id === logId ? { ...l, status: "pending" as TimeLogStatus, approvedHours: undefined } : l)),
    );
    onResetLog?.(logId);
  };

  const handleApproveMany = (logIds: string[]) => {
    setLocalLogs((prev) =>
      prev.map((l) =>
        logIds.includes(l.id) ? { ...l, status: "approved" as TimeLogStatus, approvedHours: l.actualHours } : l,
      ),
    );
    onApproveMany?.(logIds);
  };

  const handleApproveManyAtPlanned = (logIds: string[]) => {
    setLocalLogs((prev) =>
      prev.map((l) =>
        logIds.includes(l.id) ? { ...l, status: "approved" as TimeLogStatus, approvedHours: l.plannedHours } : l,
      ),
    );
    onApproveManyAtPlanned?.(logIds);
  };

  const handleSubmitPeriod = (pid: string) => {
    onSubmitPeriod?.(pid);
    setSubmitArmed(false);
  };

  if (!period) {
    return (
      <PageWrapper>
        <EmptyMessage>No payroll periods configured.</EmptyMessage>
      </PageWrapper>
    );
  }

  const isSubmitted = period.status === "Submitted";

  if (investigateEmpId) {
    const emp = empById[investigateEmpId];
    const empLogs = periodLogs
      .filter((l) => l.employeeId === investigateEmpId)
      .sort((a, b) => a.date.localeCompare(b.date));
    return (
      <PageWrapper>
        <PeriodSelector
          periods={periods}
          periodId={periodId}
          onPeriodChange={(id) => {
            setPeriodId(id);
            setInvestigateEmpId(null);
            setSearch("");
          }}
          locked={isSubmitted}
          canSubmit={false}
          onSubmit={() => undefined}
          submitArmed={false}
          setSubmitArmed={() => undefined}
          submitDisabledReason={null}
        />
        <MetricsGrid rollups={rollups} isSubmitted={isSubmitted} />
        <BackLink
          onClick={() => {
            setInvestigateEmpId(null);
            setSearch("");
          }}
        >
          ← Back to all entries
        </BackLink>
        <FocusedEmployeeView
          employee={emp}
          logs={empLogs}
          shiftById={shiftById}
          locked={isSubmitted}
          onApproveLog={handleApproveLog}
          onSetNote={handleSetNote}
          onMarkNoShow={handleMarkNoShow}
          onResetLog={handleResetLog}
        />
      </PageWrapper>
    );
  }

  const allActioned = rollups.pending === 0;
  const anyActioned = periodLogs.some((l) => l.status === "approved" || l.status === "noshow");
  const showSubmitFooter = !isSubmitted && anyActioned;

  return (
    <PageWrapper>
      {isSubmitted && (
        <SubmittedBanner>
          <EyebrowLabel>Period submitted</EyebrowLabel>
          <BannerSub>
            Locked {period.submittedAt ? new Date(period.submittedAt).toLocaleString() : ""} by{" "}
            {period.submittedBy ?? ""}. All entries are read-only.
          </BannerSub>
        </SubmittedBanner>
      )}

      <PeriodSelector
        periods={periods}
        periodId={periodId}
        onPeriodChange={(id) => {
          setPeriodId(id);
          setInvestigateEmpId(null);
          setSubmitArmed(false);
          setSearch("");
        }}
        locked={isSubmitted}
        canSubmit={allActioned}
        onSubmit={() => {
          if (submitArmed) {
            handleSubmitPeriod(period.id);
          } else {
            setSubmitArmed(true);
          }
        }}
        submitArmed={submitArmed}
        setSubmitArmed={setSubmitArmed}
        submitDisabledReason={
          rollups.pending > 0
            ? `${rollups.pending} pending entries remain — approve or mark no-show before submitting.`
            : null
        }
      />

      <MetricsGrid rollups={rollups} isSubmitted={isSubmitted} />

      {(timeOffRollup.paidEntries > 0 || timeOffRollup.unpaidEntries > 0) && (
        <TimeOffBanner>
          <div>
            <EyebrowLabel>Time off in this period</EyebrowLabel>
            <BannerSub style={{ marginTop: 4 }}>
              {timeOffRollup.paidEntries} paid · {timeOffRollup.unpaidEntries} unpaid · {fmtHours(timeOffRollup.hours)}{" "}
              covered
            </BannerSub>
          </div>
          <TimeOffCost>
            {fmtMoneyUSD(timeOffRollup.cost)}
            <TimeOffCostSub>accrual cost (paid only)</TimeOffCostSub>
          </TimeOffCost>
        </TimeOffBanner>
      )}

      <InvestigationSearch
        search={search}
        setSearch={setSearch}
        matches={searchMatches}
        onPick={(empId) => {
          setInvestigateEmpId(empId);
          setSearch("");
        }}
        placeholder={isSubmitted ? "Look up an employee in this submitted period…" : "Search employee to investigate…"}
      />

      {buckets.high.length === 0 && buckets.medium.length === 0 && buckets.clean.length === 0 ? (
        <PanelBox>
          <AllCaughtUp>
            <AllCaughtUpTitle>All caught up</AllCaughtUpTitle>
            <AllCaughtUpSub>No entries pending review for this period.</AllCaughtUpSub>
          </AllCaughtUp>
        </PanelBox>
      ) : (
        <BucketsWrapper>
          {buckets.high.length > 0 && (
            <BucketSection
              severity="high"
              logs={buckets.high}
              empById={empById}
              shiftById={shiftById}
              locked={isSubmitted}
              defaultExpanded={true}
              onInvestigate={(empId) => setInvestigateEmpId(empId)}
              onApproveLog={handleApproveLog}
              onSetNote={handleSetNote}
              onMarkNoShow={handleMarkNoShow}
              onResetLog={handleResetLog}
              onApproveMany={handleApproveMany}
              onApproveManyAtPlanned={handleApproveManyAtPlanned}
            />
          )}
          {buckets.medium.length > 0 && (
            <BucketSection
              severity="medium"
              logs={buckets.medium}
              empById={empById}
              shiftById={shiftById}
              locked={isSubmitted}
              defaultExpanded={false}
              onInvestigate={(empId) => setInvestigateEmpId(empId)}
              onApproveLog={handleApproveLog}
              onSetNote={handleSetNote}
              onMarkNoShow={handleMarkNoShow}
              onResetLog={handleResetLog}
              onApproveMany={handleApproveMany}
              onApproveManyAtPlanned={handleApproveManyAtPlanned}
            />
          )}
          {buckets.clean.length > 0 && (
            <BucketSection
              severity="clean"
              logs={buckets.clean}
              empById={empById}
              shiftById={shiftById}
              locked={isSubmitted}
              defaultExpanded={false}
              onInvestigate={(empId) => setInvestigateEmpId(empId)}
              onApproveLog={handleApproveLog}
              onSetNote={handleSetNote}
              onMarkNoShow={handleMarkNoShow}
              onResetLog={handleResetLog}
              onApproveMany={handleApproveMany}
              onApproveManyAtPlanned={handleApproveManyAtPlanned}
            />
          )}
        </BucketsWrapper>
      )}

      {showSubmitFooter && (
        <SubmitFooter $allActioned={allActioned}>
          <div>
            <EyebrowLabel>Ready to submit?</EyebrowLabel>
            <BannerSub style={{ marginTop: 4 }}>
              {allActioned
                ? "All entries actioned."
                : `${rollups.pending} pending entries remain — approve or mark no-show before submitting.`}
            </BannerSub>
          </div>
          <ActionButton
            $variant="primary"
            disabled={!allActioned}
            onClick={() => {
              if (submitArmed) {
                handleSubmitPeriod(period.id);
              } else {
                setSubmitArmed(true);
              }
            }}
          >
            {submitArmed ? "Confirm submit?" : "Submit period for payroll"}
          </ActionButton>
        </SubmitFooter>
      )}

      {isSubmitted && period.totals && (
        <RollupGrid>
          <RollupPanel title="By Client" rows={Object.entries(period.totals.byClient)} />
          <RollupPanel title="By Employee" rows={Object.entries(period.totals.byEmployee)} />
        </RollupGrid>
      )}
    </PageWrapper>
  );
};

/* ============================================================
 * Period Selector
 * ============================================================ */

function PeriodSelector({
  periods,
  periodId,
  onPeriodChange,
  locked,
  canSubmit,
  onSubmit,
  submitArmed,
  setSubmitArmed,
  submitDisabledReason,
}: {
  periods: PayrollPeriod[];
  periodId: string;
  onPeriodChange: (id: string) => void;
  locked: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  submitArmed: boolean;
  setSubmitArmed: (v: boolean) => void;
  submitDisabledReason: string | null;
}) {
  return (
    <SelectorWrapper>
      <EyebrowLabel>Period</EyebrowLabel>
      <SelectorRow>
        <PeriodButtonGroup>
          {periods.map((p) => {
            const active = p.id === periodId;
            const statusLabel = (p.status === "Open" ? "Open" : "Submitted").toUpperCase();
            return (
              <PeriodButton key={p.id} $active={active} onClick={() => onPeriodChange(p.id)}>
                <PeriodButtonLabel>{shortPeriodLabel(p)}</PeriodButtonLabel>
                <PeriodStatusLabel $active={active}>{statusLabel}</PeriodStatusLabel>
              </PeriodButton>
            );
          })}
        </PeriodButtonGroup>
        {!locked && (
          <SubmitControls>
            {submitArmed && <CancelLink onClick={() => setSubmitArmed(false)}>Cancel</CancelLink>}
            <ActionButton
              $variant="primary"
              disabled={!canSubmit}
              title={submitDisabledReason ?? undefined}
              onClick={onSubmit}
            >
              {submitArmed ? "Confirm submit?" : "Submit period"}
            </ActionButton>
          </SubmitControls>
        )}
      </SelectorRow>
    </SelectorWrapper>
  );
}

/* ============================================================
 * Metrics Grid
 * ============================================================ */

function MetricsGrid({
  rollups,
  isSubmitted,
}: {
  rollups: {
    totalEntries: number;
    employeeCount: number;
    approved: number;
    pending: number;
    highPending: number;
    totalCost: number;
  };
  isSubmitted: boolean;
}) {
  const approvedPct = rollups.totalEntries === 0 ? 0 : Math.round((rollups.approved / rollups.totalEntries) * 100);
  return (
    <MetricsGridWrapper>
      <SummaryTile
        eyebrow="Total Entries"
        value={String(rollups.totalEntries)}
        sub={`${rollups.employeeCount} employee${rollups.employeeCount === 1 ? "" : "s"}`}
      />
      <SummaryTile eyebrow="Approved" value={String(rollups.approved)} sub={`${approvedPct}% of entries`} />
      <SummaryTile
        eyebrow="Pending Review"
        value={isSubmitted ? "—" : String(rollups.pending)}
        sub={isSubmitted ? "Period closed" : `${rollups.highPending} need manual attention`}
      />
      <SummaryTile
        eyebrow={isSubmitted ? "Submitted Cost" : "Projected Cost"}
        value={fmtMoneyUSD(rollups.totalCost)}
        sub={isSubmitted ? "final approved total" : "if all approved at planned"}
      />
    </MetricsGridWrapper>
  );
}

function SummaryTile({ eyebrow, value, sub }: { eyebrow: string; value: string; sub: string }) {
  return (
    <TileBox>
      <TileEyebrow>{eyebrow}</TileEyebrow>
      <TileValue>{value}</TileValue>
      <TileSub>{sub}</TileSub>
    </TileBox>
  );
}

/* ============================================================
 * Investigation Search
 * ============================================================ */

function InvestigationSearch({
  search,
  setSearch,
  matches,
  onPick,
  placeholder,
}: {
  search: string;
  setSearch: (s: string) => void;
  matches: { employee: Employee; entries: number; approved: number }[];
  onPick: (empId: string) => void;
  placeholder?: string;
}) {
  return (
    <SearchWrapper>
      <EyebrowLabel>Investigate by employee</EyebrowLabel>
      <SearchRelative>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder ?? "Type an employee name to view and adjust individual entries…"}
        />
        {matches.length > 0 && (
          <SearchDropdown>
            {matches.map((m) => (
              <SearchDropdownItem key={m.employee.id} onClick={() => onPick(m.employee.id)}>
                <div>
                  <SearchEmpName>{m.employee.name}</SearchEmpName>
                  <SearchEmpRole>{m.employee.role}</SearchEmpRole>
                </div>
                <SearchEmpMeta>
                  {m.entries} entries · {m.approved}/{m.entries} approved
                </SearchEmpMeta>
              </SearchDropdownItem>
            ))}
          </SearchDropdown>
        )}
      </SearchRelative>
    </SearchWrapper>
  );
}

/* ============================================================
 * Bucket Section
 * ============================================================ */

function BucketSection({
  severity,
  logs,
  empById,
  shiftById,
  locked,
  defaultExpanded = false,
  onInvestigate,
  onApproveLog,
  onSetNote,
  onMarkNoShow,
  onResetLog,
  onApproveMany,
  onApproveManyAtPlanned,
}: {
  severity: TimeLogSeverity;
  logs: TimeLog[];
  empById: Record<string, Employee>;
  shiftById: Record<string, Shift>;
  locked: boolean;
  defaultExpanded?: boolean;
  onInvestigate: (empId: string) => void;
  onApproveLog: (logId: string, hours: number) => void;
  onSetNote: (logId: string, note: string | null) => void;
  onMarkNoShow: (logId: string) => void;
  onResetLog: (logId: string) => void;
  onApproveMany: (logIds: string[]) => void;
  onApproveManyAtPlanned: (logIds: string[]) => void;
}) {
  const [armBulk, setArmBulk] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(defaultExpanded);
  const meta = SEVERITY_META[severity];

  const synopsis = useMemo(() => {
    let plannedHours = 0;
    let actualHours = 0;
    let plannedCost = 0;
    let actualCost = 0;
    const byClient: Record<string, { entries: number; hours: number; cost: number }> = {};
    for (const l of logs) {
      const e = empById[l.employeeId];
      const rate = e ? hourlyRateOf(e) : 0;
      plannedHours += l.plannedHours;
      actualHours += l.actualHours;
      plannedCost += l.plannedHours * rate;
      actualCost += l.actualHours * rate;
      const slot = (byClient[l.clientName] = byClient[l.clientName] ?? {
        entries: 0,
        hours: 0,
        cost: 0,
      });
      slot.entries++;
      slot.hours += l.actualHours;
      slot.cost += l.actualHours * rate;
    }
    return {
      plannedHours,
      actualHours,
      plannedCost,
      actualCost,
      byClient: Object.entries(byClient).sort((a, b) => b[1].cost - a[1].cost),
    };
  }, [logs, empById]);

  return (
    <BucketBox>
      <BucketHeader $open={sectionOpen}>
        <BucketToggleButton onClick={() => setSectionOpen((v) => !v)}>
          <BucketChevron $open={sectionOpen}>›</BucketChevron>
          <BucketTitle>
            <SeverityDot color={severityColor[severity]} />
            {meta.label}{" "}
            <BucketSubtitle>
              · {meta.subtitle} ({logs.length} {logs.length === 1 ? "entry" : "entries"})
            </BucketSubtitle>
          </BucketTitle>
        </BucketToggleButton>
        {!locked && severity !== "high" && logs.length > 0 && (
          <ActionButton
            $variant={armBulk ? "primary" : "outline"}
            onClick={(e) => {
              e.stopPropagation();
              if (!sectionOpen) setSectionOpen(true);
              setArmBulk((v) => !v);
            }}
          >
            {armBulk ? "Cancel" : `Approve all · ${logs.length} entries`}
          </ActionButton>
        )}
      </BucketHeader>

      {sectionOpen && armBulk && !locked && (
        <BulkApproveSynopsis
          count={logs.length}
          synopsis={synopsis}
          onCancel={() => setArmBulk(false)}
          onApproveScheduled={() => {
            onApproveManyAtPlanned(logs.map((l) => l.id));
            setArmBulk(false);
          }}
          onApproveActual={() => {
            onApproveMany(logs.map((l) => l.id));
            setArmBulk(false);
          }}
        />
      )}

      {sectionOpen && (
        <div>
          {logs.map((log) => (
            <ApprovalRow
              key={log.id}
              log={log}
              employee={empById[log.employeeId]}
              shift={log.shiftId ? (shiftById[log.shiftId] ?? null) : null}
              locked={locked}
              defaultExpanded={false}
              onInvestigate={() => onInvestigate(log.employeeId)}
              onApproveLog={onApproveLog}
              onSetNote={onSetNote}
              onMarkNoShow={onMarkNoShow}
              onResetLog={onResetLog}
            />
          ))}
        </div>
      )}
    </BucketBox>
  );
}

/* ============================================================
 * Bulk Approve Synopsis
 * ============================================================ */

function BulkApproveSynopsis({
  count,
  synopsis,
  onCancel,
  onApproveScheduled,
  onApproveActual,
}: {
  count: number;
  synopsis: {
    plannedHours: number;
    actualHours: number;
    plannedCost: number;
    actualCost: number;
    byClient: [string, { entries: number; hours: number; cost: number }][];
  };
  onCancel: () => void;
  onApproveScheduled: () => void;
  onApproveActual: () => void;
}) {
  return (
    <SynopsisWrapper>
      <SynopsisEyebrow>Approve {count} entries — financial synopsis</SynopsisEyebrow>
      <SynopsisCards>
        <SynopsisCard title="At scheduled hours" hours={synopsis.plannedHours} cost={synopsis.plannedCost} />
        <SynopsisCard title="At actual hours" hours={synopsis.actualHours} cost={synopsis.actualCost} />
      </SynopsisCards>
      <ClientBreakdownLabel>By client</ClientBreakdownLabel>
      <ClientBreakdownTable>
        {synopsis.byClient.map(([client, v]) => (
          <ClientBreakdownRow key={client}>
            <ClientBreakdownName>{client}</ClientBreakdownName>
            <ClientBreakdownMeta>
              {v.entries} entries · {fmtHours(v.hours)} · {fmtMoneyUSD(v.cost)}
            </ClientBreakdownMeta>
          </ClientBreakdownRow>
        ))}
      </ClientBreakdownTable>
      <SynopsisActions>
        <ActionButton $variant="primary" onClick={onApproveScheduled}>
          Approve at scheduled
        </ActionButton>
        <ActionButton $variant="outline" onClick={onApproveActual}>
          Approve at actual
        </ActionButton>
        <ActionButton $variant="ghost" onClick={onCancel}>
          Cancel
        </ActionButton>
      </SynopsisActions>
    </SynopsisWrapper>
  );
}

function SynopsisCard({ title, hours, cost }: { title: string; hours: number; cost: number }) {
  return (
    <SynopsisCardBox>
      <SynopsisCardLabel>{title}</SynopsisCardLabel>
      <SynopsisCardHours>{fmtHours(hours)}</SynopsisCardHours>
      <SynopsisCardCost>{fmtMoneyUSD(cost)}</SynopsisCardCost>
    </SynopsisCardBox>
  );
}

/* ============================================================
 * Approval Row
 * ============================================================ */

function ApprovalRow({
  log,
  employee,
  shift,
  locked,
  defaultExpanded,
  onInvestigate,
  //   alwaysExpandable = false,
  onApproveLog,
  onSetNote,
  onMarkNoShow,
  onResetLog,
}: {
  log: TimeLog;
  employee?: Employee;
  shift: Shift | null;
  locked: boolean;
  defaultExpanded: boolean;
  onInvestigate?: () => void;
  alwaysExpandable?: boolean;
  onApproveLog: (logId: string, hours: number) => void;
  onSetNote: (logId: string, note: string | null) => void;
  onMarkNoShow: (logId: string) => void;
  onResetLog: (logId: string) => void;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isApproved = log.status === "approved";
  const isNoShow = log.status === "noshow";
  const isOrphan = !log.shiftId;
  const isNoShowSignal = !!log.shiftId && (!log.actualStart || !log.actualEnd) && log.actualHours === 0;

  const discrepancy = useMemo(() => {
    if (isOrphan) return "Orphan log — no scheduled shift";
    if (isNoShowSignal) return "No clock-in";
    if (!shift) return "";
    const startDelta = log.actualStart ? minutesBetween(shift.plannedStart, log.actualStart) : 0;
    const endDelta = log.actualEnd ? minutesBetween(shift.plannedEnd, log.actualEnd) : 0;
    const parts: string[] = [];
    if (startDelta >= 5) parts.push(`${startDelta}m late in`);
    else if (startDelta <= -5) parts.push(`${-startDelta}m early in`);
    if (endDelta <= -5) parts.push(`${-endDelta}m early out`);
    else if (endDelta >= 5) parts.push(`${endDelta}m late out`);
    if (parts.length === 0) return "On time";
    return parts.join(" · ");
  }, [isOrphan, isNoShowSignal, shift, log.actualStart, log.actualEnd]);

  if (isApproved || isNoShow) {
    const approvedHoursValue = isNoShow ? 0 : (log.approvedHours ?? log.actualHours);
    return (
      <RowWrapper>
        <ApprovedRowGrid>
          <EmpCell>
            <EmpName>{employee?.name ?? log.employeeId}</EmpName>
            <EmpRole>{employee?.role}</EmpRole>
          </EmpCell>
          <RowDate>{shortDate(log.date)}</RowDate>
          <RowClient>{log.clientName}</RowClient>
          <RowDiscrepancy>{isNoShow ? "No-show" : discrepancy || "—"}</RowDiscrepancy>
          <RowScheduled>
            {shift ? (
              <>
                {fmtRangeCompact(shift.plannedStart, shift.plannedEnd)} ·<br />
                {fmtHours(shift.plannedHours)}
              </>
            ) : (
              "—"
            )}
          </RowScheduled>
          <RowActual>
            {log.actualStart && log.actualEnd ? (
              <>
                {fmtRangeCompact(log.actualStart, log.actualEnd)} ·<br />
                {fmtHours(log.actualHours)}
              </>
            ) : (
              "—"
            )}
          </RowActual>
          <RowActions>
            <ApprovedBadge>{approvedHoursValue.toFixed(1)} H</ApprovedBadge>
            {!locked && <RevertLink onClick={() => onResetLog(log.id)}>Revert</RevertLink>}
          </RowActions>
        </ApprovedRowGrid>
      </RowWrapper>
    );
  }

  return (
    <RowWrapper>
      <PendingRowButton onClick={() => setExpanded((v) => !v)} $expanded={expanded} disabled={locked}>
        <EmpCell>
          <EmpName>{employee?.name ?? log.employeeId}</EmpName>
          <EmpRole>{employee?.role}</EmpRole>
        </EmpCell>
        <RowDate>{shortDate(log.date)}</RowDate>
        <RowClient>{log.clientName}</RowClient>
        <RowDiscrepancy $bold>{discrepancy || "—"}</RowDiscrepancy>
        <RowScheduled>
          {shift ? (
            <>
              {fmtRangeCompact(shift.plannedStart, shift.plannedEnd)} ·<br />
              {fmtHours(shift.plannedHours)}
            </>
          ) : (
            "—"
          )}
        </RowScheduled>
        <RowActual>
          {log.actualStart && log.actualEnd ? (
            <>
              {fmtRangeCompact(log.actualStart, log.actualEnd)} ·<br />
              {fmtHours(log.actualHours)}
            </>
          ) : (
            "—"
          )}
        </RowActual>
        <RowChevron $expanded={expanded}>›</RowChevron>
      </PendingRowButton>

      {expanded && (
        <InlineApprovalForm
          log={log}
          shift={shift}
          employee={employee}
          discrepancy={discrepancy}
          onApproved={() => setExpanded(false)}
          onInvestigate={onInvestigate}
          onApproveLog={onApproveLog}
          onSetNote={onSetNote}
          onMarkNoShow={onMarkNoShow}
        />
      )}
    </RowWrapper>
  );
}

/* ============================================================
 * Inline Approval Form
 * ============================================================ */

function InlineApprovalForm({
  log,
  shift,
  employee,
  discrepancy,
  onApproved,
  onInvestigate,
  onApproveLog,
  onSetNote,
  onMarkNoShow,
}: {
  log: TimeLog;
  shift: Shift | null;
  employee?: Employee;
  discrepancy: string;
  onApproved: () => void;
  onInvestigate?: () => void;
  onApproveLog: (logId: string, hours: number) => void;
  onSetNote: (logId: string, note: string | null) => void;
  onMarkNoShow: (logId: string) => void;
}) {
  const [hours, setHours] = useState<string>(shift ? shift.plannedHours.toFixed(2) : log.actualHours.toFixed(2));
  const [note, setNote] = useState<string>(log.note ?? "");

  useEffect(() => {
    setHours(shift ? shift.plannedHours.toFixed(2) : log.actualHours.toFixed(2));
    setNote(log.note ?? "");
  }, [log.id, shift]);

  const numericHours = Number(hours);
  const rate = employee ? hourlyRateOf(employee) : 0;
  console.log("InlineApprovalForm", { log, shift, employee, discrepancy, hours, numericHours, rate });

  const startDelta = shift && log.actualStart ? minutesBetween(shift.plannedStart, log.actualStart) : null;
  const endDelta = shift && log.actualEnd ? minutesBetween(shift.plannedEnd, log.actualEnd) : null;

  function commitNoteAndApprove(h: number) {
    onSetNote(log.id, note.trim() || null);
    onApproveLog(log.id, h);
    onApproved();
  }

  const isNoShowSignal = !!log.shiftId && (!log.actualStart || !log.actualEnd) && log.actualHours === 0;

  return (
    <FormPanel>
      <FormMetricRow>
        <BigDetail
          label="Scheduled"
          value={
            shift
              ? `${fmtRangeCompact(shift.plannedStart, shift.plannedEnd)} (${fmtHours(shift.plannedHours)})`
              : "No matching shift"
          }
        />
        <BigDetail
          label="Actual"
          value={log.actualStart && log.actualEnd ? fmtRangeCompact(log.actualStart, log.actualEnd) : "No-show"}
        />
        <BigDetail label="Discrepancy" value={discrepancy || "—"} />
      </FormMetricRow>

      <FormMetricRowBordered>
        <BigDetail
          label="Start delta"
          value={startDelta == null ? "—" : fmtDelta(startDelta) === "on time" ? "on time" : fmtDelta(startDelta)}
        />
        <BigDetail label="End delta" value={endDelta == null ? "—" : fmtDelta(-endDelta)} />
        <BigDetail label="Severity" value={log.severity} />
      </FormMetricRowBordered>

      <FormInputRow>
        <HoursInputGroup>
          <FormFieldLabel>Approved hours</FormFieldLabel>
          <HoursInput type="number" step="0.25" min="0" value={hours} onChange={(e) => setHours(e.target.value)} />
        </HoursInputGroup>

        <QuickSetButtons>
          {shift && (
            <QuickSetButton onClick={() => setHours(shift.plannedHours.toFixed(2))}>
              Use planned ({fmtHours(shift.plannedHours)})
            </QuickSetButton>
          )}
          {log.actualStart && log.actualEnd && (
            <QuickSetButton onClick={() => setHours(log.actualHours.toFixed(2))}>
              Use actual ({fmtHours(log.actualHours)})
            </QuickSetButton>
          )}
          {isNoShowSignal && (
            <QuickSetButton
              onClick={() => {
                onMarkNoShow(log.id);
                onApproved();
              }}
            >
              Mark no-show
            </QuickSetButton>
          )}
        </QuickSetButtons>

        <NoteGroup>
          <FormFieldLabel>Note (optional)</FormFieldLabel>
          <NoteInput
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g., approved late start — traffic incident"
          />
        </NoteGroup>

        <ApproveButton
          onClick={() => {
            const h = Number(hours);
            if (isFinite(h) && h >= 0) commitNoteAndApprove(h);
          }}
        >
          Approve
        </ApproveButton>
      </FormInputRow>

      <FormFooterCalc>
        {(isFinite(numericHours) ? numericHours : 0).toFixed(2)}h entered · 0.50h auto-break ·{" "}
        <CalcEmphasis>{Math.max(0, (isFinite(numericHours) ? numericHours : 0) - 0.5).toFixed(2)}h paid</CalcEmphasis>
      </FormFooterCalc>

      {onInvestigate && <InvestigateLink onClick={onInvestigate}>View all entries for this employee →</InvestigateLink>}
    </FormPanel>
  );
}

function BigDetail({ label, value }: { label: string; value: string }) {
  return (
    <BigDetailBox>
      <BigDetailLabel>{label}</BigDetailLabel>
      <BigDetailValue>{value}</BigDetailValue>
    </BigDetailBox>
  );
}

/* ============================================================
 * Focused Employee View
 * ============================================================ */

function FocusedEmployeeView({
  employee,
  logs,
  shiftById,
  locked,
  onApproveLog,
  onSetNote,
  onMarkNoShow,
  onResetLog,
}: {
  employee?: Employee;
  logs: TimeLog[];
  shiftById: Record<string, Shift>;
  locked: boolean;
  onApproveLog: (logId: string, hours: number) => void;
  onSetNote: (logId: string, note: string | null) => void;
  onMarkNoShow: (logId: string) => void;
  onResetLog: (logId: string) => void;
}) {
  if (!employee) {
    return <EmptyMessage>Employee not found.</EmptyMessage>;
  }

  const totalHours = logs.reduce(
    (s, l) => s + (l.status === "approved" ? (l.approvedHours ?? 0) : l.status === "noshow" ? 0 : l.actualHours),
    0,
  );
  const approved = logs.filter((l) => l.status === "approved" || l.status === "noshow").length;
  const cost = logs.reduce((s, l) => s + logCost(l, employee), 0);

  return (
    <>
      <EmployeeHeaderBox>
        <div>
          <EmployeeName>{employee.name}</EmployeeName>
          <EmployeeMeta>
            {employee.role} · {employee.type === "fixed" ? "Fixed" : "Hourly"} · {employee.id}
          </EmployeeMeta>
        </div>
        <EmployeeStats>
          <Stat label="Entries" value={String(logs.length)} />
          <Stat label="Approved" value={`${approved} / ${logs.length}`} />
          <Stat label="Hours" value={fmtHours(totalHours)} />
          <Stat label="Cost" value={fmtMoneyUSD(cost)} />
        </EmployeeStats>
      </EmployeeHeaderBox>

      <PanelBox>
        <PanelTitleBar>
          <PanelTitle>All entries in this period</PanelTitle>
          <PanelMeta>
            {logs.length} {logs.length === 1 ? "entry" : "entries"}
          </PanelMeta>
        </PanelTitleBar>
        {logs.length === 0 ? (
          <EmptyMessage>No entries for this employee in this period.</EmptyMessage>
        ) : (
          <div>
            {logs.map((log) => (
              <ApprovalRow
                key={log.id}
                log={log}
                employee={employee}
                shift={log.shiftId ? (shiftById[log.shiftId] ?? null) : null}
                locked={locked}
                defaultExpanded={false}
                alwaysExpandable
                onApproveLog={onApproveLog}
                onSetNote={onSetNote}
                onMarkNoShow={onMarkNoShow}
                onResetLog={onResetLog}
              />
            ))}
          </div>
        )}
      </PanelBox>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <StatBox>
      <StatLabel>{label}</StatLabel>
      <StatValue>{value}</StatValue>
    </StatBox>
  );
}

/* ============================================================
 * Rollup Panels (submitted period)
 * ============================================================ */

function RollupPanel({ title, rows }: { title: string; rows: [string, { hours: number; cost: number }][] }) {
  const sorted = rows.slice().sort((a, b) => b[1].cost - a[1].cost);
  const totalHours = sorted.reduce((s, [, v]) => s + v.hours, 0);
  const totalCost = sorted.reduce((s, [, v]) => s + v.cost, 0);
  return (
    <PanelBox>
      <PanelTitleBar>
        <PanelTitle>{title}</PanelTitle>
        <PanelMeta>
          {fmtHours(totalHours)} · {fmtMoneyUSD(totalCost)}
        </PanelMeta>
      </PanelTitleBar>
      {sorted.length === 0 ? (
        <EmptyMessage>No data.</EmptyMessage>
      ) : (
        <RollupTable>
          <tbody>
            {sorted.map(([name, v]) => (
              <RollupTr key={name}>
                <RollupTdName>{name}</RollupTdName>
                <RollupTdHours>{fmtHours(v.hours)}</RollupTdHours>
                <RollupTdCost>{fmtMoneyUSD(v.cost)}</RollupTdCost>
              </RollupTr>
            ))}
          </tbody>
        </RollupTable>
      )}
    </PanelBox>
  );
}

/* ============================================================
 * Styled Components
 * ============================================================ */

const C = {
  charcoal: "#111111",
  paper: "#FFFFFF",
  n100: "#F4F4F4",
  n200: "#E1E1E1",
  n300: "#CBCBCB",
  n400: "#B4B4B4",
  n500: "#555555",
  n600: "#595959",
};

const PageWrapper = styled.div`
  padding: 24px;
  background: ${C.paper};
`;

const EmptyMessage = styled.div`
  padding: 40px 16px;
  text-align: center;
  font-size: 13px;
  color: ${C.n500};
`;

const BackLink = styled.button`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 12px;
  &:hover {
    color: ${C.charcoal};
  }
`;

/* Period Selector */
const SelectorWrapper = styled.div`
  margin-bottom: 24px;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PeriodButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PeriodButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border: 1px solid ${({ $active }) => ($active ? C.charcoal : C.n200)};
  background: ${({ $active }) => ($active ? C.charcoal : C.paper)};
  color: ${({ $active }) => ($active ? C.paper : C.charcoal)};
  font-size: 12px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: ${({ $active }) => ($active ? C.charcoal : C.n100)};
  }
`;

const PeriodButtonLabel = styled.span`
  font-weight: 300;
`;

const PeriodStatusLabel = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ $active }) => ($active ? "rgba(255,255,255,0.7)" : C.n500)};
`;

const SubmitControls = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CancelLink = styled.button`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: ${C.charcoal};
  }
`;

/* Metrics */
const MetricsGridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const TileBox = styled.div`
  border: 1px solid ${C.n200};
  background: ${C.paper};
  padding: 16px;
`;

const TileEyebrow = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
`;

const TileValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${C.charcoal};
  margin-top: 6px;
  font-variant-numeric: tabular-nums;
`;

const TileSub = styled.div`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  margin-top: 4px;
`;

/* Banners */
const SubmittedBanner = styled.div`
  margin-bottom: 16px;
  border: 1px solid ${C.n300};
  background: ${C.n100};
  padding: 12px 16px;
`;

const TimeOffBanner = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${C.n200};
  background: ${C.n100};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EyebrowLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
`;

const BannerSub = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.charcoal};
`;

const TimeOffCost = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${C.charcoal};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const TimeOffCostSub = styled.div`
  font-size: 10px;
  font-weight: 300;
  color: ${C.n500};
  text-transform: none;
  letter-spacing: normal;
`;

/* Search */
const SearchWrapper = styled.div`
  margin-bottom: 24px;
`;

const SearchRelative = styled.div`
  position: relative;
  margin-top: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid ${C.n200};
  background: ${C.paper};
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 300;
  outline: none;
  &::placeholder {
    color: ${C.n400};
  }
  &:focus {
    border-color: ${C.charcoal};
  }
`;

const SearchDropdown = styled.div`
  position: absolute;
  z-index: 10;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  border: 1px solid ${C.n200};
  background: ${C.paper};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const SearchDropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid ${C.n200};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${C.n100};
  }
`;

const SearchEmpName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${C.charcoal};
`;

const SearchEmpRole = styled.div`
  font-size: 10px;
  font-weight: 300;
  color: ${C.n500};
`;

const SearchEmpMeta = styled.div`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  font-variant-numeric: tabular-nums;
`;

/* Buckets */
const BucketsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BucketBox = styled.section`
  border: 1px solid ${C.n200};
  background: ${C.paper};
`;

const BucketHeader = styled.header<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${C.n100};
  border-bottom: ${({ $open }) => ($open ? `1px solid ${C.n200}` : "none")};
`;

const BucketToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  flex: 1;
  min-width: 0;
  padding: 0;
`;

const BucketChevron = styled.span<{ $open: boolean }>`
  font-size: 14px;
  font-weight: 300;
  color: ${C.n400};
  transition: transform 0.15s;
  transform: ${({ $open }) => ($open ? "rotate(90deg)" : "rotate(0deg)")};
  display: inline-block;
`;

const SeverityDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const BucketTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.charcoal};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BucketSubtitle = styled.span`
  font-weight: 300;
  color: ${C.n500};
`;

/* Action Button */
const ActionButton = styled.button<{ $variant: "primary" | "outline" | "ghost" }>`
  padding: ${({ $variant }) => ($variant === "ghost" ? "6px 12px" : "6px 14px")};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  border: 1px solid
    ${({ $variant }) => ($variant === "primary" ? C.charcoal : $variant === "outline" ? C.n300 : "transparent")};
  background: ${({ $variant }) => ($variant === "primary" ? C.charcoal : C.paper)};
  color: ${({ $variant }) => ($variant === "primary" ? C.paper : $variant === "ghost" ? C.n500 : C.charcoal)};
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    opacity: 0.85;
  }
`;

/* Synopsis */
const SynopsisWrapper = styled.div`
  border-bottom: 1px solid ${C.n200};
  background: ${C.n100};
  padding: 16px;
`;

const SynopsisEyebrow = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
  margin-bottom: 8px;
`;

const SynopsisCards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  max-width: 680px;
`;

const SynopsisCardBox = styled.div`
  border: 1px solid ${C.n200};
  background: ${C.paper};
  padding: 12px;
`;

const SynopsisCardLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
`;

const SynopsisCardHours = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${C.charcoal};
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
`;

const SynopsisCardCost = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.charcoal};
  font-variant-numeric: tabular-nums;
`;

const ClientBreakdownLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
  margin-bottom: 4px;
`;

const ClientBreakdownTable = styled.div`
  border: 1px solid ${C.n200};
  background: ${C.paper};
  margin-bottom: 16px;
  max-width: 680px;
`;

const ClientBreakdownRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid ${C.n200};
  font-size: 11px;
  &:last-child {
    border-bottom: none;
  }
`;

const ClientBreakdownName = styled.span`
  font-weight: 300;
  color: ${C.charcoal};
`;

const ClientBreakdownMeta = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${C.n500};
`;

const SynopsisActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* Rows */
const ROW_GRID =
  "minmax(160px,1.4fr) minmax(90px,0.7fr) minmax(140px,1.1fr) minmax(140px,1.1fr) minmax(110px,0.75fr) minmax(110px,0.75fr) 140px";

const RowWrapper = styled.div`
  border-bottom: 1px solid ${C.n200};
  &:last-child {
    border-bottom: none;
  }
`;

const ApprovedRowGrid = styled.div`
  display: grid;
  grid-template-columns: ${ROW_GRID};
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: ${C.n100};
`;

const PendingRowButton = styled.button<{ $expanded: boolean }>`
  width: 100%;
  text-align: left;
  display: grid;
  grid-template-columns: ${ROW_GRID};
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: ${({ $expanded }) => ($expanded ? C.n100 : C.paper)};
  border: none;
  cursor: pointer;
  transition: background 0.12s;
  &:hover {
    background: ${C.n100};
  }
  &:disabled {
    cursor: default;
  }
`;

const EmpCell = styled.div`
  min-width: 0;
`;

const EmpName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${C.charcoal};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
`;

const EmpRole = styled.div`
  font-size: 10px;
  font-weight: 300;
  color: ${C.n500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
`;

const RowDate = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.n600};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowClient = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.n600};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowDiscrepancy = styled.div<{ $bold?: boolean }>`
  font-size: 12px;
  font-weight: ${({ $bold }) => ($bold ? 400 : 300)};
  color: ${({ $bold }) => ($bold ? C.charcoal : C.n500)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowScheduled = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.n500};
  text-align: right;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
`;

const RowActual = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.charcoal};
  text-align: right;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-self: end;
  padding-left: 8px;
`;

const ApprovedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${C.n300};
  background: ${C.paper};
  padding: 0 8px;
  min-width: 62px;
  height: 30px;
  font-size: 11px;
  font-weight: 600;
  color: ${C.n500};
  font-variant-numeric: tabular-nums;
`;

const RevertLink = styled.button`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: ${C.charcoal};
  }
`;

const RowChevron = styled.span<{ $expanded: boolean }>`
  font-size: 16px;
  font-weight: 300;
  color: ${C.n400};
  justify-self: end;
  transition: transform 0.15s;
  transform: ${({ $expanded }) => ($expanded ? "rotate(90deg)" : "rotate(0deg)")};
  display: inline-block;
`;

/* Inline Form */
const FormPanel = styled.div`
  padding: 16px;
  background: ${C.n100};
  border-top: 1px solid ${C.n200};
`;

const FormMetricRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 16px;
`;

const FormMetricRowBordered = styled(FormMetricRow)`
  padding-bottom: 16px;
  border-bottom: 1px solid ${C.n200};
  margin-bottom: 16px;
`;

const BigDetailBox = styled.div``;

const BigDetailLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
`;

const BigDetailValue = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: ${C.charcoal};
  font-variant-numeric: tabular-nums;
  margin-top: 4px;
  line-height: 1.3;
`;

const FormInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`;

const HoursInputGroup = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const FormFieldLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.n500};
  margin-bottom: 6px;
`;

const HoursInput = styled.input`
  width: 100%;
  border: 1px solid ${C.n300};
  background: ${C.paper};
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${C.charcoal};
  outline: none;
  &:focus {
    border-color: ${C.charcoal};
  }
`;

const QuickSetButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-bottom: 6px;
`;

const QuickSetButton = styled.button`
  font-size: 10px;
  font-weight: 300;
  color: ${C.charcoal};
  border: 1px solid ${C.n300};
  background: ${C.paper};
  padding: 4px 8px;
  cursor: pointer;
  &:hover {
    background: ${C.n100};
  }
`;

const NoteGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoteInput = styled.input`
  width: 100%;
  border: 1px solid ${C.n300};
  background: ${C.paper};
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 300;
  color: ${C.charcoal};
  outline: none;
  &::placeholder {
    color: ${C.n400};
  }
  &:focus {
    border-color: ${C.charcoal};
  }
`;

const ApproveButton = styled.button`
  flex-shrink: 0;
  background: ${C.charcoal};
  color: ${C.paper};
  padding: 8px 24px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.88;
  }
`;

const FormFooterCalc = styled.div`
  font-size: 10px;
  font-weight: 300;
  color: ${C.n500};
  font-variant-numeric: tabular-nums;
  margin-top: 8px;
`;

const CalcEmphasis = styled.span`
  font-weight: 600;
  color: ${C.charcoal};
`;

const InvestigateLink = styled.button`
  margin-top: 10px;
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: ${C.charcoal};
  }
`;

/* Employee Focus View */
const EmployeeHeaderBox = styled.div`
  border: 1px solid ${C.n200};
  background: ${C.paper};
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EmployeeName = styled.div`
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${C.charcoal};
`;

const EmployeeMeta = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.n500};
  margin-top: 4px;
`;

const EmployeeStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 24px;
  text-align: right;
`;

const StatBox = styled.div``;

const StatLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${C.n500};
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${C.charcoal};
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
`;

/* Panel */
const PanelBox = styled.div`
  border: 1px solid ${C.n200};
  background: ${C.paper};
`;

const PanelTitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${C.n200};
  background: ${C.n100};
`;

const PanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C.charcoal};
`;

const PanelMeta = styled.div`
  font-size: 11px;
  font-weight: 300;
  color: ${C.n500};
`;

/* Submit footer */
const SubmitFooter = styled.div<{ $allActioned: boolean }>`
  margin-top: 24px;
  border: 1px solid ${({ $allActioned }) => ($allActioned ? C.charcoal : C.n200)};
  background: ${({ $allActioned }) => ($allActioned ? C.n100 : C.paper)};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/* Rollup panels */
const RollupGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 24px;
`;

const RollupTable = styled.table`
  width: 100%;
  font-size: 12px;
`;

const RollupTr = styled.tr`
  border-bottom: 1px solid ${C.n200};
  &:last-child {
    border-bottom: none;
  }
`;

const RollupTdName = styled.td`
  padding: 8px 16px;
  font-size: 12px;
  color: ${C.charcoal};
`;

const RollupTdHours = styled.td`
  padding: 8px 16px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  color: ${C.n500};
  width: 80px;
`;

const RollupTdCost = styled.td`
  padding: 8px 16px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: ${C.charcoal};
  width: 110px;
`;

const AllCaughtUp = styled.div`
  padding: 40px 16px;
  text-align: center;
`;

const AllCaughtUpTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${C.charcoal};
`;

const AllCaughtUpSub = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${C.n500};
  margin-top: 4px;
`;
